import { useState, useMemo, useCallback, useEffect } from "react";
import Button from "@mui/material/Button";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import ListAltIcon from "@mui/icons-material/ListAlt";

import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import Spinner from "@components/Spinner";
import {
  fetchInvoicesReview,
  generateInvoices,
  cancelGeneratedInvoices,
} from "@service/arInvoiceApiService";
import { InvoicePreviewModal } from "@components/ar_invoice_components/InvoicePreviewModal";
import { InvoiceDetailReportModal } from "@components/ar_invoice_components/InvoiceDetailReportModal";

interface InvoiceRow {
  "Invoice ID": number | null;
  "Invoice #": string | null;
  "Plant ID": number | null;
  Plant: string | null;
  "Facility ID": number | null;
  Facility: string | null;
  "Invoice Code": string | null;
  Contractors: number | null;
  Total: number | null;
  "Billed Week": string | null;
  Status: string | null;
}

function getEndDateStr(startStr: string): string {
  const start = new Date(startStr + "T00:00:00");
  start.setDate(start.getDate() + 6);
  const year = start.getFullYear();
  const month = String(start.getMonth() + 1).padStart(2, "0");
  const day = String(start.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Invoices can only be generated/canceled for the current or previous week —
// not for periods that start before the previous week's Sunday.
function isSelectablePeriod(periodStart: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayOfWeek = today.getDay();
  const currentWeekSunday = new Date(today);
  currentWeekSunday.setDate(today.getDate() - dayOfWeek);
  const previousWeekSunday = new Date(currentWeekSunday);
  previousWeekSunday.setDate(currentWeekSunday.getDate() - 7);
  const selected = new Date(periodStart + "T00:00:00");
  return selected.getTime() >= previousWeekSunday.getTime();
}

const currencyFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  return `$${val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtMoney = (val: number | null | undefined): string => {
  if (val == null) return "$0.00";
  return `$${val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const safeSizeColumnsToFit = (params: { api?: any }) => {
  const api = params.api;
  if (!api || api.isDestroyed?.()) return;
  try {
    api.sizeColumnsToFit();
  } catch {
    /* grid not ready / no longer mounted — ignore */
  }
};

const StatusCellRenderer = (props: { value: string }) => {
  const cls =
    props.value === "Generated"
      ? "tw-bg-green-100 tw-text-green-800"
      : props.value === "Review"
      ? "tw-bg-yellow-100 tw-text-yellow-800"
      : props.value === "Voided"
      ? "tw-bg-red-100 tw-text-red-800"
      : "";
  return (
    <div
      className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-px-2 ${cls}`}
    >
      {props.value}
    </div>
  );
};

interface InvoicesTabProps {
  periodStart: string;
  // Bumped by the parent whenever an invoice detail is finalized/canceled, so
  // the Invoices grid repopulates (un-invoiced "Review" groups changed).
  refreshSignal?: number;
  // Called after invoices are generated, so the parent can refresh the
  // Invoice Details tab (details are now linked to an invoice).
  onAfterGenerate?: () => void;
}

export function InvoicesTab({
  periodStart,
  refreshSignal,
  onAfterGenerate,
}: InvoicesTabProps) {
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<InvoiceRow[]>([]);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [previewInvoiceId, setPreviewInvoiceId] = useState<number | null>(null);
  const [reportInvoiceId, setReportInvoiceId] = useState<number | null>(null);

  const fetchData = useCallback(async (startStr: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchInvoicesReview({
        start_date: startStr,
        end_date: getEndDateStr(startStr),
      });
      setRows(Array.isArray(response.data) ? response.data : []);
      setSelectedRows([]);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to fetch invoices");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Populate on page render (the component stays mounted in a hidden panel),
  // when the pay period changes, and when a detail is finalized/canceled
  // (refreshSignal) — NOT on every tab switch. Generation refetches explicitly
  // via handleConfirmGenerate.
  useEffect(() => {
    fetchData(periodStart);
  }, [periodStart, refreshSignal, fetchData]);

  // Status first in the fixed order Review → Generated → Voided; then invoice
  // number; then plant ascending; within a plant, facility / invoice code
  // ascending.
  const displayRows = useMemo(() => {
    const statusOrder: Record<string, number> = {
      Review: 0,
      Generated: 1,
      Voided: 2,
    };
    const statusRank = (s: string | null) =>
      statusOrder[s ?? ""] ?? Number.MAX_SAFE_INTEGER;
    return [...rows].sort((a, b) => {
      const statusDiff = statusRank(a.Status) - statusRank(b.Status);
      if (statusDiff !== 0) return statusDiff;
      const invDiff = (a["Invoice #"] ?? "").localeCompare(
        b["Invoice #"] ?? ""
      );
      if (invDiff !== 0) return invDiff;
      const plantDiff =
        (a["Plant ID"] ?? Infinity) - (b["Plant ID"] ?? Infinity);
      if (plantDiff !== 0) return plantDiff;
      const codeDiff = (a["Invoice Code"] ?? "").localeCompare(
        b["Invoice Code"] ?? ""
      );
      if (codeDiff !== 0) return codeDiff;
      return (a.Facility ?? "").localeCompare(b.Facility ?? "");
    });
  }, [rows]);

  // Derive from displayRows so the confirmation modal lists groups in the same
  // ascending order they'll be generated in.
  const reviewRows = useMemo(
    () => displayRows.filter((r) => r.Status === "Review"),
    [displayRows]
  );

  const reviewTotal = useMemo(
    () => reviewRows.reduce((sum, r) => sum + (r.Total ?? 0), 0),
    [reviewRows]
  );

  const handleConfirmGenerate = async () => {
    setGenerating(true);
    setGenerateError(null);
    try {
      await generateInvoices({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
      });
      setConfirmOpen(false);
      await fetchData(periodStart);
      onAfterGenerate?.();
    } catch (err: any) {
      // Keep the modal open and surface the backend message (e.g. plants
      // missing company/client configuration).
      setGenerateError(
        err?.response?.data?.error || "Failed to generate invoices."
      );
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = useCallback((invoiceId: number) => {
    setPreviewInvoiceId(invoiceId);
  }, []);

  const handleDetailReport = useCallback((invoiceId: number) => {
    setReportInvoiceId(invoiceId);
  }, []);

  const generatedSelectedRows = useMemo(
    () =>
      selectedRows.filter(
        (r) => r["Invoice ID"] != null && r.Status === "Generated"
      ),
    [selectedRows]
  );

  const hasNonGeneratedSelection = useMemo(
    () => selectedRows.some((r) => r.Status !== "Generated"),
    [selectedRows]
  );

  const hasVoidedSelection = useMemo(
    () => selectedRows.some((r) => (r.Status ?? "").toLowerCase() === "voided"),
    [selectedRows]
  );

  // Invoices can only be generated/canceled for the current or previous week.
  const canAct = isSelectablePeriod(periodStart);

  const handleConfirmCancelGenerated = async () => {
    setCanceling(true);
    try {
      await cancelGeneratedInvoices({
        invoice_ids: generatedSelectedRows
          .map((r) => r["Invoice ID"])
          .filter((id): id is number => id != null),
      });
      setCancelConfirmOpen(false);
      await fetchData(periodStart);
      onAfterGenerate?.();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to cancel invoices.");
    } finally {
      setCanceling(false);
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "",
        field: "Preview",
        width: 110,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) =>
          params.data?.["Invoice ID"] != null ? (
            <span>
              <Tooltip title="Preview invoice">
                <IconButton
                  size="small"
                  sx={{ color: "#0d9488" }}
                  onClick={() => handlePreview(params.data["Invoice ID"])}
                >
                  <FindInPageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Detail report">
                <IconButton
                  size="small"
                  sx={{ color: "#2642c9" }}
                  onClick={() => handleDetailReport(params.data["Invoice ID"])}
                >
                  <ListAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          ) : null,
      },
      { field: "Invoice #", headerName: "Invoice #", width: 160 },
      { field: "Plant", headerName: "Plant", width: 220 },
      { field: "Facility", headerName: "Facility", width: 160 },
      { field: "Invoice Code", headerName: "Inv. Code", width: 120 },
      { field: "Contractors", headerName: "Contractors", width: 130 },
      {
        field: "Total",
        headerName: "Total",
        width: 140,
        valueFormatter: currencyFormatter,
      },
      { field: "Billed Week", headerName: "Billed Week", width: 130 },
      {
        field: "Status",
        headerName: "Status",
        width: 120,
        pinned: "right",
        cellRenderer: StatusCellRenderer,
      },
    ],
    [handlePreview, handleDetailReport]
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: true, minWidth: 90 }),
    []
  );

  return (
    <div className="tw-w-full">
      {error && (
        <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
          <div>{error}</div>
        </div>
      )}

      {isLoading && (
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
          <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
          Loading invoices...
        </div>
      )}

      {!isLoading && (
        <div className="tw-bg-white tw-p-4">
          <div
            className="ag-theme-balham tw-w-full"
            style={{ height: Math.min(rows.length * 28 + 56, 500) }}
          >
            <AgGridReact
              rowData={displayRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onSelectionChanged={(event) => {
                const selectedNodes = event.api.getSelectedNodes();
                setSelectedRows(selectedNodes.map((node: any) => node.data));
              }}
              animateRows={true}
              pagination={false}
              overlayNoRowsTemplate="<span></span>"
              onGridReady={safeSizeColumnsToFit}
              onGridSizeChanged={safeSizeColumnsToFit}
            />
          </div>
          <div className="tw-border-l tw-border-r tw-border-b tw-border-gray-300 tw-p-1 tw-bg-white tw-flex tw-gap-2 tw-items-center">
            <Button
              color="primary"
              sx={{ bgcolor: "#2642c9", textTransform: "none" }}
              variant="contained"
              startIcon={<ReceiptLongIcon />}
              onClick={() => {
                setGenerateError(null);
                setConfirmOpen(true);
              }}
              disabled={
                !canAct ||
                reviewRows.length === 0 ||
                hasVoidedSelection ||
                generatedSelectedRows.length > 0
              }
            >
              Generate Invoice
            </Button>
            <Button
              color="error"
              sx={{ textTransform: "none" }}
              variant="contained"
              onClick={() => setCancelConfirmOpen(true)}
              disabled={
                !canAct ||
                generatedSelectedRows.length === 0 ||
                hasNonGeneratedSelection
              }
            >
              Cancel Generated
            </Button>
            {!canAct && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                Cannot generate or cancel invoices for a past pay period
              </span>
            )}
            {canAct && reviewRows.length === 0 && (
              <span className="tw-text-gray-500 tw-text-sm">
                No review groups to generate
              </span>
            )}
            {hasVoidedSelection && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                Unselect rows with status "Voided" to generate invoices
              </span>
            )}
            {generatedSelectedRows.length > 0 && hasNonGeneratedSelection && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                Select only "Generated" invoices to cancel
              </span>
            )}
          </div>
        </div>
      )}

      {/* Generate confirmation modal */}
      <Dialog
        open={confirmOpen}
        onClose={() => !generating && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Invoice Generation</DialogTitle>
        <DialogContent dividers>
          {generateError && (
            <div className="tw-mb-3 tw-p-2 tw-rounded tw-bg-red-100 tw-text-red-700 tw-text-sm">
              {generateError}
            </div>
          )}
          <p className="tw-mb-3">
            You are about to generate <strong>{reviewRows.length}</strong>{" "}
            invoice(s) for the following Plant / Facility groups:
          </p>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Plant</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Facility</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Contractors
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewRows.map((r, idx) => (
                <TableRow key={`${r["Plant ID"]}-${r["Facility ID"]}-${idx}`}>
                  <TableCell>{r.Plant ?? "—"}</TableCell>
                  <TableCell>{r.Facility ?? "—"}</TableCell>
                  <TableCell align="right">{r.Contractors ?? 0}</TableCell>
                  <TableCell align="right">{fmtMoney(r.Total)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell />
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {reviewRows.reduce((s, r) => s + (r.Contractors ?? 0), 0)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmtMoney(reviewTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={generating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            onClick={handleConfirmGenerate}
            disabled={generating}
            startIcon={
              generating ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <ReceiptLongIcon />
              )
            }
          >
            {generating ? "Generating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel generated confirmation modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => !canceling && setCancelConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Cancel Generated Invoices</DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-3">
            You are about to cancel{" "}
            <strong>{generatedSelectedRows.length}</strong> generated
            invoice(s). Their invoice details will be voided.
          </p>
          <ul className="tw-list-disc tw-pl-5 tw-space-y-1">
            {generatedSelectedRows.map((row) => (
              <li key={String(row["Invoice ID"])}>
                {row["Invoice #"] ?? "(No Invoice #)"} - {row.Plant ?? "-"} -{" "}
                {row.Facility ?? "-"}
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            disabled={canceling}
            sx={{ textTransform: "none" }}
          >
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={handleConfirmCancelGenerated}
            disabled={canceling}
          >
            {canceling ? "Canceling..." : "Confirm Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      {previewInvoiceId != null && (
        <InvoicePreviewModal
          invoiceId={previewInvoiceId}
          onClose={() => setPreviewInvoiceId(null)}
        />
      )}

      {reportInvoiceId != null && (
        <InvoiceDetailReportModal
          invoiceId={reportInvoiceId}
          onClose={() => setReportInvoiceId(null)}
        />
      )}
    </div>
  );
}

export default InvoicesTab;
